import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './AISupportChat.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * AISupportChat - Context-Aware AI Chat Component
 *
 * Supports different bot modes:
 * - sales: Lead generation, product questions, pricing
 * - support: Help, troubleshooting, billing
 * - wizard: Setup guidance, step-by-step help
 */
const AISupportChat = ({
  isOpen,
  onClose,
  botMode = 'support',
  customGreeting = null,
  customSuggestions = null,
  pageContext = ''
}) => {
  const { getTokens, currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showEscalate, setShowEscalate] = useState(false);
  const [emailCollected, setEmailCollected] = useState(false);
  const [awaitingEmail, setAwaitingEmail] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const prevBotModeRef = useRef(botMode);

  // Get header title based on mode
  const getHeaderTitle = () => {
    switch (botMode) {
      case 'sales':
        return 'Sales Assistant';
      case 'wizard':
        return 'Setup Guide';
      case 'support':
      default:
        return 'AI Support';
    }
  };

  // Get header color based on mode - using brand indigo/purple colors
  const getHeaderStyle = () => {
    switch (botMode) {
      case 'sales':
        return { background: 'linear-gradient(135deg, #E0C98E, #C9A227)' };
      case 'wizard':
        return { background: 'linear-gradient(135deg, #C9A227, #D4B560)' };
      case 'support':
      default:
        return { background: 'linear-gradient(135deg, #C9A227, #E0C98E)' };
    }
  };

  // Initialize chat with welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const greeting = customGreeting ||
        `Hi${currentUser ? ` ${currentUser.firstName}` : ''}! I'm your Canira AI assistant. I can help you with:\n\n- Creating and managing webinars\n- Pricing and billing questions\n- Technical troubleshooting\n- Feature explanations\n\nHow can I help you today?`;

      const welcomeMessage = {
        id: Date.now(),
        role: 'assistant',
        content: greeting,
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);

      // Use custom suggestions if provided, otherwise fetch from API
      if (customSuggestions) {
        setSuggestions(customSuggestions);
      } else {
        fetchSuggestions();
      }
    }
  }, [isOpen, currentUser, messages.length, customGreeting, customSuggestions]);

  // Reset chat when bot mode changes
  useEffect(() => {
    if (prevBotModeRef.current !== botMode && messages.length > 0) {
      // Bot mode changed, reset the chat
      setMessages([]);
      setSessionId(null);
      setShowEscalate(false);
      setEmailCollected(false);
      setAwaitingEmail(false);
    }
    prevBotModeRef.current = botMode;
  }, [botMode, messages.length]);

  // Update suggestions when customSuggestions change
  useEffect(() => {
    if (customSuggestions) {
      setSuggestions(customSuggestions);
    }
  }, [customSuggestions]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const fetchSuggestions = async () => {
    try {
      const response = await fetch(`${API_URL}/ai-support/suggestions?mode=${botMode}`);
      const data = await response.json();
      if (data.success) {
        setSuggestions(data.suggestions.slice(0, 4));
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const sendMessage = useCallback(async (messageText) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setSuggestions([]);

    // Handle email collection for sales mode
    if (awaitingEmail && botMode === 'sales') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(messageText.trim())) {
        setEmailCollected(true);
        setAwaitingEmail(false);
        // Send lead to backend
        try {
          const token = getTokens()?.accessToken;
          await fetch(`${API_URL}/ai-support/lead`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` }),
            },
            body: JSON.stringify({
              email: messageText.trim(),
              source: pageContext,
              botMode
            }),
          });
        } catch (error) {
          console.error('Error saving lead:', error);
        }

        const thankYouMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: "Thanks! I've saved your email. Our team will reach out soon. Is there anything else you'd like to know about Canira?",
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, thankYouMessage]);
        setIsLoading(false);
        return;
      } else {
        const invalidEmailMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: "That doesn't look like a valid email address. Could you please enter your email?",
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, invalidEmailMessage]);
        setIsLoading(false);
        return;
      }
    }

    try {
      const token = getTokens()?.accessToken;
      const response = await fetch(`${API_URL}/ai-support/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          message: messageText.trim(),
          sessionId,
          botMode,
          pageContext
        }),
      });

      const data = await response.json();

      if (data.success) {
        const assistantMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: data.message,
          timestamp: new Date(),
          suggestedActions: data.suggestedActions,
          sources: Array.isArray(data.sources) ? data.sources : [],
        };

        setMessages(prev => [...prev, assistantMessage]);
        setSessionId(data.sessionId);

        if (data.needsEscalation) {
          setShowEscalate(true);
        }

        // Check if we should prompt for email (sales mode)
        if (data.collectEmail && botMode === 'sales' && !emailCollected) {
          setAwaitingEmail(true);
        }
      } else {
        throw new Error(data.message || 'Failed to get response');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again or contact support directly at support@canira.io",
        timestamp: new Date(),
        isError: true,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, isLoading, getTokens, awaitingEmail, botMode, emailCollected, pageContext]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion.text);
  };

  const handleActionClick = (action) => {
    // Handle suggested actions
    switch (action.toLowerCase()) {
      case 'view pricing':
        window.open('/pricing', '_blank');
        break;
      case 'create webinar':
        window.open('/dashboard/webinars/create', '_blank');
        break;
      case 'start free trial':
        window.open('/register', '_blank');
        break;
      case 'schedule demo':
      case 'get demo':
        if (!emailCollected) {
          setAwaitingEmail(true);
          const demoMessage = {
            id: Date.now(),
            role: 'assistant',
            content: "Great! I'd be happy to help schedule a demo. What's your email address so our team can reach out?",
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, demoMessage]);
        }
        break;
      // Help articles live at /help (public). Video tutorials are Canira
      // University, which needs an account — visitors get the help center.
      case 'view tutorials':
        window.open(currentUser ? '/university' : '/help', '_blank');
        break;
      case 'browse help center':
      case 'browse help':
        window.open('/help', '_blank');
        break;
      case 'create ticket':
      case 'contact support':
        setShowEscalate(true);
        break;
      default:
        sendMessage(action);
    }
  };

  const handleEscalate = async () => {
    try {
      const token = getTokens()?.accessToken;
      if (!token) {
        setMessages(prev => [...prev, {
          id: Date.now(),
          role: 'assistant',
          content: 'Please log in to create a support ticket, or email us at support@canira.io',
          timestamp: new Date(),
        }]);
        setShowEscalate(false);
        return;
      }

      const response = await fetch(`${API_URL}/ai-support/escalate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          sessionId,
          reason: 'User requested human support',
          chatHistory: messages,
          botMode,
          pageContext
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMessages(prev => [...prev, {
          id: Date.now(),
          role: 'assistant',
          content: `I've escalated your request to our support team. Ticket ID: ${data.ticketId}\n\nExpected response time: ${data.estimatedResponse}\n\nWe'll get back to you as soon as possible!`,
          timestamp: new Date(),
        }]);
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: 'Unable to create ticket. Please email support@canira.io directly.',
        timestamp: new Date(),
        isError: true,
      }]);
    }
    setShowEscalate(false);
  };

  const handleClearChat = async () => {
    if (sessionId) {
      try {
        const token = getTokens()?.accessToken;
        await fetch(`${API_URL}/ai-support/chat/clear`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          body: JSON.stringify({ sessionId }),
        });
      } catch (error) {
        console.error('Error clearing chat:', error);
      }
    }
    setMessages([]);
    setSessionId(null);
    setShowEscalate(false);
    setAwaitingEmail(false);

    if (customSuggestions) {
      setSuggestions(customSuggestions);
    } else {
      fetchSuggestions();
    }
  };

  const formatMessage = (content) => {
    // Simple markdown-like formatting
    return content.split('\n').map((line, i) => {
      // Handle numbered lists
      if (/^\d+\.\s/.test(line)) {
        return <div key={i} className="message-list-item">{line}</div>;
      }
      // Handle bullet points
      if (/^[-*]\s/.test(line)) {
        return <div key={i} className="message-bullet">{line}</div>;
      }
      // Handle bold text with emoji headers
      if (/^[1-9]️⃣/.test(line)) {
        return <div key={i} className="message-step">{line}</div>;
      }
      // Handle empty lines
      if (!line.trim()) {
        return <br key={i} />;
      }
      return <div key={i}>{line}</div>;
    });
  };

  if (!isOpen) return null;

  return (
    <div className={`ai-support-chat ${botMode}-mode`}>
      <div className="chat-header" style={getHeaderStyle()}>
        <div className="chat-header-info">
          <div className="chat-avatar">
            {botMode === 'sales' && '💼'}
            {botMode === 'wizard' && '🧙‍♂️'}
            {botMode === 'support' && (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
            )}
          </div>
          <div>
            <h3>{getHeaderTitle()}</h3>
            <span className="status-indicator online">Online</span>
          </div>
        </div>
        <div className="chat-header-actions">
          <button onClick={handleClearChat} className="btn-icon" title="Clear chat">
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
            </svg>
          </button>
          <button onClick={onClose} className="btn-icon" title="Close">
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="chat-messages">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`message ${message.role} ${message.isError ? 'error' : ''}`}
          >
            <div className="message-content">
              {formatMessage(message.content)}
            </div>
            {message.suggestedActions && message.suggestedActions.length > 0 && (
              <div className="message-actions">
                {message.suggestedActions.map((action, idx) => (
                  <button
                    key={idx}
                    className="action-btn"
                    onClick={() => handleActionClick(action)}
                  >
                    {action}
                  </button>
                ))}
              </div>
            )}
            {message.sources && message.sources.length > 0 && (
              <div className="message-sources">
                <span className="message-sources-label">Read more:</span>
                {message.sources.map((source) => (
                  <a
                    key={source.slug}
                    className="message-source-pill"
                    href={`/help/${source.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {source.title} →
                  </a>
                ))}
              </div>
            )}
            <span className="message-time">
              {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        ))}

        {isLoading && (
          <div className="message assistant loading">
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {suggestions.length > 0 && messages.length <= 1 && (
        <div className="chat-suggestions">
          <p>Quick questions:</p>
          <div className="suggestions-grid">
            {suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                className="suggestion-btn"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion.icon && <span className="suggestion-icon">{suggestion.icon}</span>}
                {suggestion.text}
              </button>
            ))}
          </div>
        </div>
      )}

      {showEscalate && (
        <div className="escalate-prompt">
          <p>Would you like to speak with a human support agent?</p>
          <div className="escalate-actions">
            <button onClick={handleEscalate} className="btn-primary">
              Yes, create ticket
            </button>
            <button onClick={() => setShowEscalate(false)} className="btn-secondary">
              Continue with AI
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="chat-input-form">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={awaitingEmail ? "Enter your email address..." : "Type your message..."}
          disabled={isLoading}
        />
        <button type="submit" disabled={isLoading || !inputValue.trim()} style={getHeaderStyle()}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
          </svg>
        </button>
      </form>
      <div className="chat-help-center-link">
        <a href="/help" target="_blank" rel="noopener noreferrer">Browse all help articles →</a>
      </div>
    </div>
  );
};

export default AISupportChat;
